import { useEffect, useRef, useState, MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { Building2 } from 'lucide-react';
import {
    getMRIMachineMetadataOptions,
    MRIMachineMetadataOptionsDTO,
} from '../../../services/apiService';
import { Input } from '../../ui/input';
import FormCard from './FormCard';
import {
    FormField,
    FormItem,
    FormLabel,
    FormControl,
    FormMessage,
} from '../../ui/form';
import { DocumentRichText } from '../../documents/form/inputs/DocumentRichText';
import ImageUpload from '../../general-components/ImageUpload';
import { CategoryPicker } from '../../ui-components/category/CategoryPicker';
import { Category } from '../../ui-components/category/categoryColors';
import { LocationPicker } from '../LocationPicker';
import { MachineFormValues } from '../types';

interface RoomDetailsCardProps {
    isNew: boolean;
    imageUrl?: string;
    onRenameCategory: (rename: { from: Category; to: Category }) => void;
}

export default function RoomDetailsCard({
    isNew,
    imageUrl,
    onRenameCategory,
}: RoomDetailsCardProps) {
    const { t } = useTranslation();
    const form = useFormContext<MachineFormValues>();
    const roomNameRef = useRef<HTMLInputElement>(null);
    const [metadata, setMetadata] = useState<MRIMachineMetadataOptionsDTO>({
        locations: [],
        categories: [],
    });

    useEffect(() => {
        roomNameRef.current?.focus();
    }, []);

    useEffect(() => {
        const load = async () => {
            try {
                setMetadata(await getMRIMachineMetadataOptions());
            } catch (error) {
                console.error('Error loading room metadata:', error);
            }
        };
        load();
    }, []);

    return (
        <FormCard
            icon={Building2}
            title={t('roomOverview.mriMachineForm.sections.basic')}
            description={t(
                'roomOverview.mriMachineForm.sections.basicDescription'
            )}
        >
            <div className="grid gap-5 sm:grid-cols-2">
                <FormField
                    control={form.control}
                    name="Name"
                    rules={{ required: 'Room name is required' }}
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel className="text-xs" required>
                                {t(
                                    'roomOverview.mriMachineForm.form.roomName.label'
                                )}
                            </FormLabel>
                            <FormControl>
                                <Input
                                    {...field}
                                    ref={(e) => {
                                        field.ref(e);
                                        (
                                            roomNameRef as MutableRefObject<HTMLInputElement | null>
                                        ).current = e;
                                    }}
                                    placeholder={t(
                                        'roomOverview.mriMachineForm.form.roomName.info'
                                    )}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormItem className="flex flex-col h-[220px] sm:h-auto sm:row-span-3">
                    <FormLabel className="text-xs">
                        {t('roomOverview.mriMachineForm.sections.machineImage')}
                    </FormLabel>
                    <ImageUpload
                        className="flex-1 min-h-0"
                        imageUrl={imageUrl}
                        onImageUploaded={(fileId) =>
                            form.setValue('machineImage', fileId || '')
                        }
                        labels={{
                            dragDrop: t(
                                'roomOverview.mriMachineForm.form.machineImage.dragDrop'
                            ),
                            dropHere: t(
                                'roomOverview.mriMachineForm.form.machineImage.dropHere'
                            ),
                            dropToReplace: t(
                                'roomOverview.mriMachineForm.form.machineImage.dropToReplace'
                            ),
                            change: t(
                                'roomOverview.mriMachineForm.form.machineImage.change'
                            ),
                            formats: t(
                                'roomOverview.mriMachineForm.form.machineImage.formats'
                            ),
                            imageAlt: t(
                                'roomOverview.mriMachineForm.form.machineImage.label'
                            ),
                        }}
                    />
                </FormItem>

                <FormField
                    control={form.control}
                    name="location"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel className="text-xs">
                                {t(
                                    'roomOverview.mriMachineForm.form.location.label'
                                )}
                            </FormLabel>
                            <FormControl>
                                <LocationPicker
                                    value={field.value}
                                    onChange={field.onChange}
                                    existing={metadata.locations}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="category"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel className="text-xs">
                                {t(
                                    'roomOverview.mriMachineForm.form.category.label'
                                )}
                            </FormLabel>
                            <FormControl>
                                <CategoryPicker
                                    value={field.value}
                                    onChange={field.onChange}
                                    existing={metadata.categories}
                                    mode={isNew ? 'create' : 'edit'}
                                    onRename={onRenameCategory}
                                    className="h-10 max-w-none border-control bg-background"
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="notes"
                    render={({ field }) => (
                        <FormItem className="flex flex-col h-[220px] sm:col-span-2">
                            <FormLabel className="text-xs">
                                {t(
                                    'roomOverview.mriMachineForm.sections.notes'
                                )}
                            </FormLabel>
                            <div className="flex-1 min-h-0">
                                <FormControl>
                                    <DocumentRichText
                                        value={field.value}
                                        onChange={field.onChange}
                                        placeholder={t(
                                            'roomOverview.mriMachineForm.form.notes.placeholder'
                                        )}
                                        className="flex h-full flex-col overflow-hidden border-control bg-background [&>div:first-child]:bg-input [&>div:last-child]:min-h-0 [&>div:last-child]:flex-1 [&>div:last-child]:overflow-y-auto [&>div:last-child]:[scrollbar-width:none]"
                                    />
                                </FormControl>
                            </div>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>
        </FormCard>
    );
}
